import type { IntlShape } from 'react-intl-next';

import type { EmojiNodeDataProvider } from '@atlaskit/editor-plugin-emoji';
import type { UserPreferencesProvider as UserPreferencesProviderNext } from '@atlaskit/editor-common/user-preferences';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';
import type { FindReplaceOptions } from '@atlaskit/editor-plugins/find-replace';
import type { TaskDecisionProvider } from '@atlaskit/task-decision/types';
import type { MediaPluginOptions } from '@atlaskit/editor-plugins/media/types';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { EmojiProvider } from '@atlaskit/emoji';
import type { MentionProvider } from '@atlaskit/mention/resource';
import type {
	ContextIdentifierProvider,
	AutoformattingProvider,
} from '@atlaskit/editor-common/provider-factory';
import type { MentionPluginConfig } from '@atlaskit/editor-plugins/mentions';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { EventHandlers } from '@atlaskit/editor-common/ui';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import type { LimitedModePluginOptions } from '@atlaskit/editor-plugin-limited-mode';
import type { LoomPluginOptionsWithoutProvider } from '@atlaskit/editor-plugin-loom';
import type { MentionsChangedHandler } from '@atlaskit/editor-plugin-mentions';
import type { EngagementPlatformPluginOptions } from '@atlaskit/editor-plugin-engagement-platform';
import type { ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel';
import type { SelectionExtensionPluginOptions } from '@atlaskit/editor-plugin-selection-extension';
import type { DiffParams } from '@atlaskit/editor-plugin-show-diff';
import type { SyncedBlockProvider } from '@atlaskit/editor-synced-block-provider';
import { getSyncedBlockRenderer } from '@atlaskit/editor-synced-block-renderer';
import type {
	UserPreferencesProvider,
	LinkingOptions,
	EmptyStateHandler,
	QuickInsertOptions,
	EditorContentMode,
} from '@atlaskit/editor-common/types';
import type { AnnotationProviders } from '@atlaskit/editor-plugins/annotation';
import type { CollabEditOptions } from '@atlaskit/editor-common/collab';
import type { TrackChangesPluginOptions } from '@atlaskit/editor-plugin-track-changes';
import { mediaEditingPlugin } from '@atlaskit/editor-plugin-media-editing';
import { nativeEmbedsPlugin } from '@atlassian/editor-plugin-native-embeds';

import type { AIDefinitionsPluginOptions } from '@atlassian/editor-plugin-ai-definitions';
import type { AIPluginOptions } from '@atlassian/editor-plugin-ai';
import { aiSuggestionsPlugin } from '@atlassian/editor-plugin-ai-suggestions';
import { aiAutocompletePlugin } from '@atlassian/editor-plugin-ai-autocomplete';
import { malleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import { companyHubSectionCtaPlugin } from '@atlassian/editor-plugin-company-hub-section-cta';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';
import { fullPagePreset } from '@atlassian/confluence-presets/full-page';
import { forgeEditorPlugin } from '@atlassian/xen-editor-provider';

import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { getUnsupportedContent } from '@confluence/fabric-extension-lib/entry-points/getUnsupportedContent.ts';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import { fg } from '@confluence/feature-gating';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage/entry-points/editorOfflineStoragePlugin.ts';
import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links/entry-points/headingLinksPlugin.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { createRemixContentComponent } from '@confluence/malleable-ui-common/entry-points/EditorRemixCreator.ts';

import { conflictResolutionUIPlugin } from './plugins/conflictResolutionUIPlugin/index.ts';
import { emptyStatePlugin } from './plugins/emptyStatePlugin.ts';
import { ssrPlugin } from './plugins/ssrPlugin.ts';
import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin.ts';

/**
 * IMPORTANT: Don't use optional properties in this interface because it reduces the type safety.
 *            Use `undefined` instead to indicate that the property is not set.
 */
export interface CreateFullPageEditorPresetProps {
	__livePage: boolean | undefined;
	__rendererExtensionOptions: ExtensionPluginOptions['__rendererExtensionOptions'] | undefined;
	allowFindReplace: boolean | FindReplaceOptions | undefined;
	annotationProviders: Pick<AnnotationProviders, 'annotationManager' | 'inlineComment'> | undefined;
	appearance: FullPageAppearances;
	autoformattingProvider: Promise<AutoformattingProvider> | undefined;
	collabEdit: CollabEditOptions | undefined;
	contentId: string | undefined;
	spaceKey: string | undefined;
	contextIdentifierProvider: Promise<ContextIdentifierProvider> | undefined;
	contextPanelOptions: Pick<ContextPanelPluginOptions, 'objectSideBar'> | undefined;
	editorEngagementPlatformPluginConfig: Pick<EngagementPlatformPluginOptions, 'coordinationClient'>;
	editorPluginAIDefinitionsConfig: AIDefinitionsPluginOptions;
	editorPluginAIDefinitionsEnabled: boolean;
	editorPluginAIOptions: AIPluginOptions;
	elementBrowser:
		| {
				emptyStateHandler?: EmptyStateHandler;
		  }
		| undefined;
	emojiProvider: Promise<EmojiProvider> | undefined;
	emojiNodeDataProvider: EmojiNodeDataProvider | undefined;
	extensionHandlers: ExtensionHandlers | undefined;
	flags: FlagsStateContainer | undefined;
	getExtensionHeight: GetPMNodeHeight | undefined;
	getHeadingLinkPath: () => string | null;
	handleMentionsChanged: MentionsChangedHandler;
	currentUserId: string | undefined;
	hasEditPermission: boolean | undefined;
	hasRequestedEditPermission: boolean | undefined;
	intl: IntlShape;
	isAIEnabledForContent: boolean;
	isCompanyHub: boolean;
	isDesktop: boolean;
	isLoomPluginEnabled: boolean;
	limitedModePluginOptions: LimitedModePluginOptions | undefined;
	linking: Pick<LinkingOptions, 'linkPicker' | 'smartLinks'> | undefined;
	loomPluginOptions: Pick<LoomPluginOptionsWithoutProvider, 'renderButton'>;
	media: Pick<MediaPluginOptions, 'provider' | 'syncProvider'> | undefined;
	mention: Pick<MentionPluginConfig, 'profilecardProvider'> | undefined;
	mentionProvider: Promise<MentionProvider> | undefined;
	prevAppearance: FullPageAppearances | undefined;
	quickInsert: QuickInsertOptions | undefined;
	referentialityContext: ReferentialityContext;
	requestToEditContent: (() => void) | undefined;
	selectionExtensionPluginOptions: SelectionExtensionPluginOptions;
	taskDecisionProvider: Promise<TaskDecisionProvider> | undefined;
	userPreferencesProvider: UserPreferencesProvider | undefined;
	userPreferencesProviderNext: UserPreferencesProviderNext | undefined;
	viewMode: ViewMode | undefined;
	contextualFormattingEnabled: boolean;
	disabled?: boolean;
	blockLinkHashPrefix: string | undefined;
	showDiffPluginParams: DiffParams | undefined;
	syncedBlockProvider: SyncedBlockProvider | undefined;
	syncedBlockEventHandlers: EventHandlers | undefined;
	disableFloatingToolbar: boolean | undefined;
	disableToolbar: boolean | undefined;
	trackChangesPluginOptions: TrackChangesPluginOptions | undefined;
	contentMode: EditorContentMode | undefined;
	enablePlaceholderLoadingSpinner?: boolean;
	isEmbeddedConfluence: boolean;
	moveWhiteboardToPage?: (whiteboardContentId: string, targetParentPageId: string) => Promise<void>;
}

/**
 * This function creates a full page editor preset with the provided options.
 *
 * If you want to do any changes, first of all, read https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4239829169/Editor+RFC+059+Editor+Presets+Package.
 *
 * HOW TO MAKE CHANGES:
 * 1. Do not use optional properties in the `CreateFullPageEditorPresetProps` interface.
 * 2. Keep this function as small and simple as possible. Pass only the necessary parameters to the plugin options:
 *   - If a plugin parameter doesn't need any data from Confluence (e.g. option is just a static value),
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - If a plugin parameter depends on Statsig experiment value, FG value,
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - Pass parameters from this function to the plugin options ONLY if there is NO OTHER WAY to do it directly in the plugin options.
 *   - Pass only NECESSARY parameters to the plugin options, AVOID passing the WHOLE OBJECT with all properties.
 *   - Do NOT USE SPREAD operator (`...props`) to pass plugin options, it can make the code less readable and harder to maintain.
 */
// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function createFullPageEditorPreset(options: CreateFullPageEditorPresetProps): any {
	const { intl, appearance, prevAppearance } = options;

	return fullPagePreset({
		intl,
		pluginOptions: {
			contentFormat: expValEquals('confluence_compact_text_format', 'isEnabled', true)
				? {
						initialContentMode: options.contentMode ?? 'standard',
					}
				: undefined,
			base: {
				__livePage: options.__livePage,
			},
			helpDialog: {
				imageUploadProviderExists: false,
				aiEnabled: options.isAIEnabledForContent ?? false,
			},
			hyperlink: {
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				onClickCallback: options.linking?.smartLinks?.onClickCallback,
			},
			quickInsert: {
				emptyStateHandler: options.elementBrowser?.emptyStateHandler,
				quickInsert: options.quickInsert,
			},
			placeholder: {
				viewMode: options.viewMode,
				isAIEnabled: options.isAIEnabledForContent ?? false,
				// hide placeholder when (1) experiment enabled (2) not embedded editor & (3) user would see BPQA.
				// match BPQA conditions as defined by shouldRenderBPQA in FullPageEditorComponent
				// in this case, only isCompanyHub is not handled yet by the placeholder plugin
				isPlaceholderHidden:
					!options.isEmbeddedConfluence &&
					!options.isCompanyHub &&
					(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
						expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2')),
				enableLoadingSpinner: options.enablePlaceholderLoadingSpinner,
			},
			selection: {
				__livePage: options.__livePage,
			},
			editorDisabled: {
				disabled: options.disabled,
			},
			breakout: {
				editorAppearance: appearance,
			},
			expand: {
				editorAppearance: appearance,
				__livePage: options.__livePage,
			},
			annotation: {
				annotationManager: options.annotationProviders?.annotationManager,
				createCommentExperience: createInlineCommentCompoundExperience,
				selectCommentExperience: selectInlineCommentCompoundExperience,
				viewInlineCommentTraceUFOPress: () => traceUFOPress('view-inline-comment-action'),
			},
			media: {
				createCommentExperience: fg('confluence_fe_create_inline_comment_exp_coverage_2')
					? createInlineCommentCompoundExperience
					: undefined,
				editorAppearance: appearance,
			},
			mentions: {
				handleMentionsChanged: options.handleMentionsChanged,
				currentUserId: options.currentUserId,
			},
			table: {
				editorAppearance: appearance,
				prevEditorAppearance: prevAppearance,
			},
			tasksAndDecisions: {
				hasEditPermission: options.hasEditPermission,
				requestToEditContent: options.requestToEditContent,
				hasRequestedEditPermission: options.hasRequestedEditPermission,
			},
			collabEdit: {
				collabEdit: options.collabEdit,
				__livePage: options.__livePage,
			},
			// Remove the options.contextPanelOptions undefined checking after cleaning `platform_editor_ai_object_sidebar_injection` FG
			// and options.contextPanelOptions will be always defined.
			contextPanel: options.contextPanelOptions
				? {
						objectSideBar: options.contextPanelOptions.objectSideBar,
					}
				: undefined,
			extension: {
				extensionHandlers: options.extensionHandlers,
				editorAppearance: appearance,
				__rendererExtensionOptions: options.__rendererExtensionOptions,
				getExtensionHeight: options.getExtensionHeight,
				getUnsupportedContent,
			},
			layout: {
				editorAppearance: appearance,
			},
			card: {
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				onClickCallback: options.linking?.smartLinks?.onClickCallback,
				__livePage: options.__livePage,
				CompetitorPrompt,
			},
			insertBlock: {
				editorAppearance: appearance,
				toolbarButtons: {
					emoji: { enabled: true, showAt: 'lg' },
					insert: { enabled: true, showAt: 'lg' },
					layout: { enabled: true, showAt: 'lg' },
					media: { enabled: true, showAt: 'lg' },
					mention: { enabled: true, showAt: 'lg' },
					table: { enabled: true, showAt: 'md' },
					taskList: { enabled: true, showAt: 'lg' },
				},
			},
			avatarGroup: {
				// Avatars are moved to Confluence codebase for Edit in Context
				// When Edit in Context is enabled primaryToolbarComponents is undefined
				// For more details please check
				// https://hello.atlassian.net/wiki/spaces/PCG/pages/2851572180/Editor+toolbar+for+live+pages+and+edit+in+context+projects
				collabEdit: options.collabEdit,
			},
			codeBidiWarning: {
				editorAppearance: appearance,
			},
			referentiality: {
				referentialityContext: options.referentialityContext,
			},
			ai: options.editorPluginAIOptions,
			loom: {
				renderButton: options.loomPluginOptions.renderButton,
			},
			editorViewMode: {
				viewMode: options.viewMode,
			},
			aiDefinitions: options.editorPluginAIDefinitionsConfig,
			selectionMarker: {
				__livePage: options.__livePage ?? false,
			},
			engagementPlatform: {
				coordinationClient: options.editorEngagementPlatformPluginConfig.coordinationClient,
			},
			selectionExtension: fg('platform_editor_controls_selection_extension')
				? options.selectionExtensionPluginOptions
				: {},
			metrics:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				expValEquals('platform_editor_controls', 'cohort', 'variant1')
					? { userPreferencesProvider: options.userPreferencesProvider }
					: undefined,
			primaryToolbar: {
				contextualFormattingEnabled: options.contextualFormattingEnabled,
			},
			selectionToolbar: {
				contextualFormattingEnabled: options.contextualFormattingEnabled,
			},
			blockMenu: {
				getLinkPath: options.getHeadingLinkPath,
				blockLinkHashPrefix: options.blockLinkHashPrefix,
			},
			showDiff: options.showDiffPluginParams,
			syncedBlock:
				options.syncedBlockProvider && expValEquals('platform_synced_block', 'isEnabled', true)
					? {
							enableSourceCreation: true,
							syncedBlockRenderer: getSyncedBlockRenderer({
								syncBlockRendererOptions: {
									eventHandlers: options.syncedBlockEventHandlers,
									stickyHeaders: {
										show: true,
										offsetTop: 0,
									},
								},
							}),
							syncBlockDataProvider: options.syncedBlockProvider,
						}
					: undefined,
			limitedMode: options.limitedModePluginOptions || {},
			toolbar: {
				contextualFormattingEnabled: 'controlled',
			},
			trackChanges: {
				ButtonWrapper: options.trackChangesPluginOptions?.ButtonWrapper,
			},
		},
		providers: {
			userPreferencesProvider: options.userPreferencesProvider,
			userPreferencesProviderNext: options.userPreferencesProviderNext,
			cardProvider: options.linking?.smartLinks?.provider,
			contextIdentifierProvider: options.contextIdentifierProvider,
			inlineCommentAnnotationProvider: options.annotationProviders?.inlineComment,
			mediaProvider: options.media?.provider,
			syncMediaProvider: options.media?.syncProvider,
			mentionProvider: options.mentionProvider,
			profilecardProvider: options.mention?.profilecardProvider,
			emojiProvider: options.emojiProvider,
			emojiNodeDataProvider: options.emojiNodeDataProvider,
			collabEditProvider: options.collabEdit?.provider,
			autoformattingProvider: options.autoformattingProvider,
			taskDecisionProvider: options.taskDecisionProvider,
		},
		enabledOptionalPlugins: {
			limitedMode: false,
			findReplace: Boolean(options.allowFindReplace),
			referentiality: Boolean(options.referentialityContext.plugin),
			loom: options.isLoomPluginEnabled,
			aiDefinitions: options.editorPluginAIDefinitionsEnabled,
			connectivity:
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			metrics: fg('platform_editor_ease_of_use_metrics'),
			codeBlockAdvanced: true,
			selectionExtension: expValEquals('platform_editor_controls', 'cohort', 'variant1'),
			aiExperience: Boolean(options.editorPluginAIOptions.aiGlobalOptIn.status !== 'disabled'),
			interaction:
				Boolean(options?.__livePage) ||
				expValEquals('platform_editor_no_cursor_on_edit_page_init', 'isEnabled', true),
			userPreferences: fg('platform_editor_use_preferences_plugin'),
			trackChanges: fg('platform_editor_ai_aifc_streaming'),
			showDiff: Boolean(options?.showDiffPluginParams) || fg('platform_editor_ai_aifc_streaming'),
			toolbar:
				!options?.disableToolbar &&
				(fg('platform_editor_ai_aifc_streaming') ||
					(options.editorPluginAIOptions.aiGlobalOptIn.status !== 'enabled' &&
						expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true))),
			blockMenu: expValEquals('platform_editor_block_menu', 'isEnabled', true),
			localId: fg('platform_editor_adf_with_localid'),
			aiStreamingOrchestrator: fg('platform_editor_ai_aifc_streaming'),
			syncedBlock: expValEquals('platform_synced_block', 'isEnabled', true),
			floatingToolbar: !options?.disableFloatingToolbar,
			codeBidiWarning: !expValEquals('platform_editor_remove_bidi_char_warning', 'isEnabled', true),
			contentFormat: expValEquals('confluence_compact_text_format', 'isEnabled', true),
			uiControlRegistry: expValEquals('platform_editor_paste_actions_menu', 'isEnabled', true),
		},
	})
		.add(ssrPlugin)
		.add([
			EditorHeadingLinksPlugin,
			{
				getHeadingLinkPath: options.getHeadingLinkPath,
			},
		])
		.add(pageTitleUtilsPlugin)
		.add(fabricExtensionManagerPlugin)
		.add(forgeEditorPlugin)
		.maybeAdd(
			[editorOfflineStoragePlugin, { flags: options.flags }],
			options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
		)
		.maybeAdd(
			[
				conflictResolutionUIPlugin,
				{
					flags: options.flags,
					contentId: options.contentId,
					spaceKey: options.spaceKey,
				},
			],
			options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
		)
		.add(emptyStatePlugin)
		.add(legacyContentMacroPlugin)
		.maybeAdd(mediaEditingPlugin, expVal('platform_editor_add_image_editing', 'isEnabled', false))
		.maybeAdd(companyHubSectionCtaPlugin, options.isCompanyHub)
		.maybeAdd(aiSuggestionsPlugin, expVal('platform_editor_ai_suggestions', 'isEnabled', false))
		.maybeAdd(
			[
				malleableUiPlugin,
				{
					createRemixContentComponent,
					diagramPaste:
						options.moveWhiteboardToPage && options.spaceKey
							? {
									getCurrentPageId: () => options.contentId,
									moveWhiteboardToPage: options.moveWhiteboardToPage,
									resolveContentSpaceKey: async (contentId: string) => {
										try {
											const response = await fetch(
												`/wiki/rest/api/content/${contentId}?expand=space`,
												{ credentials: 'include' },
											);
											if (!response.ok) {
												return null;
											}
											const data = await response.json();
											return data?.space?.key ?? null;
										} catch {
											return null;
										}
									},
									targetSpaceKey: options.spaceKey,
								}
							: undefined,
				},
			],
			expVal('cc-maui-experiment', 'isEnabled', false),
		)
		.maybeAdd([nativeEmbedsPlugin], expVal('platform_editor_native_embeds', 'isEnabled', false))
		.maybeAdd(
			aiAutocompletePlugin,
			expVal('platform_editor_ai_headingautocomplete', 'isEnabled', false),
		);
}
